import { useRef, useState } from 'react'
import SelectRefiner from '../../../shared/refiners/select-refiner/SelectRefiner'
import InputRefiner from '../../../shared/refiners/input-refiner/InputRefiner'
import CertificateListService from '../../../services/CertificateListService'
import classes from './CertificateProductListRefiners.module.scss'
import { useEffect } from 'react'
import certProdListRefinerColumns from '../../../shared/refiners/data/cert-prod-list-refiner-columns'
import CheckboxRefiner from '../../../shared/refiners/checkbox-refiner/CheckboxRefiner'
import CalendarRefiner from '../../../shared/refiners/calendar-refiner/CalendarRefiner'
import useDebounce from '../../../hooks/useDebounce'

const MODEL_NAME_PAGE_COUNT = 100

const CertificateProductListRefiners = props => {
  const [certStatusOptions, setCertStatusOptions] = useState([])
  const [prodStatusOptions, setProdStatusOptions] = useState([])
  const [modelNameOptions, setModelNameOptions] = useState([])
  const [modelNameAllOptions, setModelNameAllOptions] = useState([])
  const [modelNameSearch, setModelNameSearch] = useState('')
  const [modelNameLoadedSearch, setModelNameLoadedSearch] = useState(null)
  const [modelNameCurrentPage, setModelNameCurrentPage] = useState(1)
  const [hasMoreModelNameOptions, setHasMoreModelNameOptions] = useState(true)
  const [ownerNameOptions, setOwnerNameOptions] = useState([])
  const [ownerNameEmptyMessage, setOwnerNameEmptyMessage] = useState(null)
  const [collectionStatusOptions, setCollectionStatusOptions] = useState([])
  const [productTypeOptions, setProductTypeOptions] = useState([])
  const [productTypeAllOptions, setProductTypeAllOptions] = useState([])
  const [isModelNameLoading, setIsModelNameLoading] = useState(false)
  const [isOwnerNameLoading, setIsOwnerNameLoading] = useState(false)
  const [isProductTypeLoading, setIsProductTypeLoading] = useState(false)
  const modelNameSearchCache = useRef(new Map())
  const currentModelNameSearch = useRef('')
  const currentOwnerNameSearch = useRef('')

  const {
    refiners,
    addSelectedToYourFilters,
    replaceFilterBySelected,
    onClickRemoveRefiner,
    onClickRemoveRefinerGroup,
    onChangeDatePickerRefiner,
  } = props

  useEffect(() => {
    setCertStatusOptions(['Active', 'Obsolete', 'Under Revision', 'Withdrawn'])

    setProdStatusOptions(['Active', 'Draft', 'InActive'])
    setCollectionStatusOptions(['Active', 'Draft', 'InActive', 'Recommendation Submitted', '(No Value)'])
  }, [])

  useEffect(() => {
    const loadModelNameOptions = async () => {
      setIsModelNameLoading(true)

      try {
        const response = await CertificateListService.getAssets({
          PageCount: MODEL_NAME_PAGE_COUNT,
          CurrentPage: 1,
          CustomFilters: [],
          Refiners: [],
        })
        const options = Array.from(
          new Set(
            (response?.data ?? [])
              .map(item => item.modelName)
              .filter(Boolean)
          )
        ).map(name => ({ name }))

        const hasMoreOptions = (response?.data ?? []).length === MODEL_NAME_PAGE_COUNT
        modelNameSearchCache.current.set('', { options, currentPage: 1, hasMoreOptions })

        if (currentModelNameSearch.current === '') {
          setModelNameAllOptions(options)
          setModelNameLoadedSearch('')
          setHasMoreModelNameOptions(hasMoreOptions)
        }
      } finally {
        setIsModelNameLoading(false)
      }
    }

    loadModelNameOptions()
  }, [])

  useEffect(() => {
    if (modelNameLoadedSearch !== modelNameSearch) {
      setModelNameOptions([])
      return
    }

    const filteredOptions = modelNameAllOptions.filter(item =>
      item.name.toLowerCase().includes(modelNameSearch)
    )

    setModelNameOptions(filteredOptions)
  }, [modelNameAllOptions, modelNameLoadedSearch, modelNameSearch, refiners])

  useEffect(() => {
    setIsProductTypeLoading(true)
    CertificateListService.getProductTypes()
      .then(response => {
        if (response && response.isSuccess) {
          const options = response.data.map(item => ({ name: item.hierarchyValue }))
          setProductTypeAllOptions(options)
          setProductTypeOptions(options)
        }
      })
      .finally(() => setIsProductTypeLoading(false))
  }, [])

  const debouncedModelNameSearch = useDebounce(async search => {
    const cachedResult = modelNameSearchCache.current.get(search)

    if (cachedResult) {
      setModelNameAllOptions(cachedResult.options)
      setModelNameLoadedSearch(search)
      setModelNameCurrentPage(cachedResult.currentPage)
      setHasMoreModelNameOptions(cachedResult.hasMoreOptions)
      setIsModelNameLoading(false)
      return
    }

    setIsModelNameLoading(true)

    try {
      const response = await CertificateListService.getAssets({
        PageCount: MODEL_NAME_PAGE_COUNT,
        CurrentPage: 1,
        GlobalSearch: search,
        CustomFilters: [],
        Refiners: [],
      })
      const responseData = response?.data ?? []
      const options = Array.from(
        new Set(
          responseData
            .map(item => item.modelName)
            .filter(Boolean)
        )
      ).map(name => ({ name }))
      const hasMoreOptions = responseData.length === MODEL_NAME_PAGE_COUNT

      modelNameSearchCache.current.set(search, { options, currentPage: 1, hasMoreOptions })

      if (currentModelNameSearch.current === search) {
        setModelNameAllOptions(options)
        setModelNameLoadedSearch(search)
        setModelNameCurrentPage(1)
        setHasMoreModelNameOptions(hasMoreOptions)
      }
    } finally {
      setIsModelNameLoading(false)
    }
  }, 500)

  const onChangeModelNameFilterText = event => {
    const search = (event.filter ?? '').toLowerCase()
    const cachedResult = modelNameSearchCache.current.get(search)

    currentModelNameSearch.current = search
    setModelNameSearch(search)

    if (cachedResult) {
      setModelNameAllOptions(cachedResult.options)
      setModelNameLoadedSearch(search)
      setModelNameCurrentPage(cachedResult.currentPage)
      setHasMoreModelNameOptions(cachedResult.hasMoreOptions)
      setIsModelNameLoading(false)
      return
    }

    setModelNameLoadedSearch(null)
    setModelNameAllOptions([])
    setHasMoreModelNameOptions(false)
    debouncedModelNameSearch(search)
  }

  const onShowMoreModelNames = async () => {
    if (isModelNameLoading || !hasMoreModelNameOptions) return

    setIsModelNameLoading(true)

    try {
      const nextPage = modelNameCurrentPage + 1
      const response = await CertificateListService.getAssets({
        PageCount: MODEL_NAME_PAGE_COUNT,
        CurrentPage: nextPage,
        GlobalSearch: modelNameSearch,
        CustomFilters: [],
        Refiners: [],
      })
      const responseData = response?.data ?? []
      const nextOptions = responseData
        .map(item => item.modelName)
        .filter(Boolean)
        .map(name => ({ name }))

      setModelNameAllOptions(currentOptions => {
        const uniqueNames = new Set(currentOptions.map(item => item.name))
        const uniqueNextOptions = nextOptions.filter(item => {
          if (uniqueNames.has(item.name)) return false

          uniqueNames.add(item.name)
          return true
        })

        const options = [...currentOptions, ...uniqueNextOptions]
        const hasMoreOptions = responseData.length === MODEL_NAME_PAGE_COUNT

        modelNameSearchCache.current.set(modelNameSearch, {
          options,
          currentPage: nextPage,
          hasMoreOptions,
        })
        return options
      })
      setModelNameCurrentPage(nextPage)
      setHasMoreModelNameOptions(responseData.length === MODEL_NAME_PAGE_COUNT)
    } finally {
      setIsModelNameLoading(false)
    }
  }

  const modelNameOptionTemplate = option => (
    <span className="model-name-refiner-option" title={option.name}>
      {option.name}
    </span>
  )

  const debouncedOwnerNameSearch = useDebounce(async search => {
    setIsOwnerNameLoading(true)

    try {
      const response = await CertificateListService.getCompannyAddresses({
        data: {
          sortOrder: '',
          companyName: [
            {
              operator: 'like',
              companyNameList: [search],
            },
          ],
        },
      })
      const finalResult = Array.from(
        new Set(
          (response?.data?.companyAddressDetails ?? [])
            .map(item => item.companyName?.trim())
            .filter(Boolean)
        )
      ).map(name => ({ name, code: name }))

      if (currentOwnerNameSearch.current === search) setOwnerNameOptions(finalResult)
    } finally {
      setIsOwnerNameLoading(false)
    }
  }, 500)

  const onChangeOwnerNameFilterText = event => {
    const search = (event.filter ?? '').toLowerCase()

    currentOwnerNameSearch.current = search

    if (search.length < 3) {
      setOwnerNameEmptyMessage('Enter minimum of "3" characters')
      setOwnerNameOptions([])
      setIsOwnerNameLoading(false)
      return
    }

    setOwnerNameEmptyMessage(null)
    debouncedOwnerNameSearch(search)
  }

  // Filters the Product Type options fetched from CertificateListService.getProductTypes
  const onChangeProductTypeFilterText = event => {
    const search = (event.filter ?? '').toLowerCase()
    const finalResult = productTypeAllOptions.filter(item =>
      item.name.toLowerCase().includes(search)
    )
    setProductTypeOptions(finalResult)
  }

  return (
    <div className={classes['cert-refiners-section']}>
      <div className="ul-card -app">
        <div
          className={`ul-card__item -bordered ul-card__head font-size-12 ${classes['select-a-filter-text']}`}
        >
          Select a filter below to refine your search
        </div>
      </div>

      <div className={classes['cert-refiners-accordion']}>
        <CheckboxRefiner
          refiners={refiners}
          refinerLabel={certProdListRefinerColumns[0].Value}
          options={certStatusOptions}
          addSelectedToYourFilters={v => {
            addSelectedToYourFilters(v, certProdListRefinerColumns[0].Id)
          }}
          onClickRemoveRefiner={onClickRemoveRefiner}
          refinerId={certProdListRefinerColumns[0].Id}
          onClickRemoveRefinerGroup={onClickRemoveRefinerGroup}
        />

        <SelectRefiner
          refinerLabel={certProdListRefinerColumns.find(c => c.Id === 'modelName').Value}
          options={modelNameOptions}
          addSelectedToYourFilters={v => {
            addSelectedToYourFilters(v, 'modelName')
          }}
          isFilterOn={true}
          isMultiSelect={true}
          onFilter={onChangeModelNameFilterText}
          isRefinerLoading={isModelNameLoading}
          showMore
          hasMoreOptions={hasMoreModelNameOptions}
          onShowMore={onShowMoreModelNames}
          preserveRowsOnOptionsChange
          preserveSelectedValues
          refiners={refiners}
          refinerId="modelName"
          multiSelectClassName="model-name-refiner"
          multiSelectPanelClassName="model-name-refiner-panel"
          optionItemTemplate={modelNameOptionTemplate}
          placeHolder='-- Select -- '
          panelAppendTo={document.body}
        />

        <CheckboxRefiner
          refiners={refiners}
          refinerLabel={certProdListRefinerColumns.find(c => c.Id === 'status').Value}
          options={prodStatusOptions}
          addSelectedToYourFilters={v => {
            addSelectedToYourFilters(v, 'status')
          }}
          onClickRemoveRefiner={onClickRemoveRefiner}
          refinerId="status"
          onClickRemoveRefinerGroup={onClickRemoveRefinerGroup}
        />

        <SelectRefiner
          refinerLabel={certProdListRefinerColumns.find(c => c.Id === 'ownerName').Value}
          options={ownerNameOptions}
          addSelectedToYourFilters={v => {
            addSelectedToYourFilters(v, 'ownerName')
          }}
          isFilterOn={true}
          isMultiSelect={false}
          onFilter={onChangeOwnerNameFilterText}
          isRefinerLoading={isOwnerNameLoading}
          customEmptyMessage={ownerNameEmptyMessage}
          closePanelOnScroll
          preserveSelectedValues
          refiners={refiners}
          refinerId="ownerName"
          placeHolder='-- Select -- '
        />

        <InputRefiner
          refinerLabel={certProdListRefinerColumns.find(c => c.Id === 'projectNumber').Value}
          refinerId="projectNumber"
          refiners={refiners}
          onSubmitValue={replaceFilterBySelected}
          onClickRemoveRefinerGroup={onClickRemoveRefinerGroup}
          placeHolder='Enter 3 characters and press enter'
          minLength={3}
        />

        <InputRefiner
          refinerLabel={certProdListRefinerColumns.find(c => c.Id === 'collectionName').Value}
          refinerId="collectionName"
          refiners={refiners}
          onSubmitValue={replaceFilterBySelected}
          onClickRemoveRefinerGroup={onClickRemoveRefinerGroup}
          placeHolder='Enter 3 characters and press enter'
          minLength={3}
        />

        <CheckboxRefiner
          refiners={refiners}
          refinerLabel={certProdListRefinerColumns.find(c => c.Id === 'collectionStatus').Value}
          options={collectionStatusOptions}
          addSelectedToYourFilters={v => {
            addSelectedToYourFilters(v, 'collectionStatus')
          }}
          onClickRemoveRefiner={onClickRemoveRefiner}
          refinerId="collectionStatus"
          onClickRemoveRefinerGroup={onClickRemoveRefinerGroup}
        />

        <InputRefiner
          refinerLabel={certProdListRefinerColumns.find(c => c.Id === 'modelNomenclature').Value}
          refinerId="modelNomenclature"
          refiners={refiners}
          onSubmitValue={replaceFilterBySelected}
          onClickRemoveRefinerGroup={onClickRemoveRefinerGroup}
          placeHolder='Enter 3 characters and press enter'
          minLength={3}
        />

        <SelectRefiner
          refinerLabel={certProdListRefinerColumns.find(c => c.Id === 'productType').Value}
          options={productTypeOptions}
          addSelectedToYourFilters={v => {
            addSelectedToYourFilters(v, 'productType')
          }}
          isFilterOn={true}
          isMultiSelect={true}
          onFilter={onChangeProductTypeFilterText}
          isRefinerLoading={isProductTypeLoading}
          showMore
          preserveRowsOnOptionsChange
          panelAppendTo={document.body}
          multiSelectPanelClassName="product-type-refiner-panel"
          preserveSelectedValues
          refiners={refiners}
          refinerId="productType"
          placeHolder='-- Select -- '
        />

        <InputRefiner
          refinerLabel={certProdListRefinerColumns.find(c => c.Id === 'referenceNumber').Value}
          refinerId="referenceNumber"
          refiners={refiners}
          onSubmitValue={replaceFilterBySelected}
          onClickRemoveRefinerGroup={onClickRemoveRefinerGroup}
          placeHolder='Enter 3 characters and press enter'
          minLength={3}
        />

        <InputRefiner
          refinerLabel={certProdListRefinerColumns.find(c => c.Id === 'familySeries').Value}
          refinerId="familySeries"
          refiners={refiners}
          onSubmitValue={replaceFilterBySelected}
          onClickRemoveRefinerGroup={onClickRemoveRefinerGroup}
          placeHolder='Enter 3 characters and press enter'
          minLength={3}
        />

        <CalendarRefiner
          refiners={refiners}
          refinerLabel={certProdListRefinerColumns.find(c => c.Id === 'creationDate').Value}
          refinerId="creationDate"
          onChangeDatePickerRefiner={v => {
            onChangeDatePickerRefiner(v, 'creationDate')
          }}
          onClickRemoveRefinerGroup={onClickRemoveRefinerGroup}
        />
      </div>
    </div>
  )
}

export default CertificateProductListRefiners
